import { useCombobox } from "downshift";
import { useEffect, useState } from "react";
import styled from "styled-components";
import { theme } from "..";
import { Icon } from "../icons";
import { CreateTransient } from "../../shared/types";

type DropdownItemId = string | number | null;

export type DropdownItem = {
  toString: () => string;
  id: DropdownItemId;
};

export type DropdownWithSearchProps<T extends DropdownItem> = {
  searchPlaceholder: string;
  noItemsFoundPlaceholder: string;
  selectedItemId: DropdownItemId;
  items: T[];
  filterItems: (value: string) => T[];
  itemToString: (item: T | null) => string;
  onSelectedItemChange: (selectedItem: T | null) => void;
};

export const DropdownWithSearch = <T extends DropdownItem>(
  props: DropdownWithSearchProps<T>,
) => {
  const [items, setItems] = useState<T[]>([]);

  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    getInputProps,
    getItemProps,
    selectedItem,
    selectItem,
    highlightedIndex,
  } = useCombobox({
    items,
    onInputValueChange: ({ inputValue }) => {
      if (items.length === 0) {
        // first render causes bugs where psychologists can only see themselves
        setItems(props.items);
        return;
      }
      const filteredItems = props.filterItems(inputValue);
      setItems(filteredItems);
    },
    itemToString: props.itemToString,
    onSelectedItemChange: ({ selectedItem }) => {
      props.onSelectedItemChange(selectedItem as T);
    },
  });

  // I have tried to use this advice: https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  // But I don't think it will work, since props.items will be always different from items,
  // especially when items are filtered.
  // Not using useEffect otherwise require consumer to use key prop, which is not ideal.
  // I also have tried to manage items completely outside, but that didn't really worked either.
  useEffect(() => {
    setItems(props.items);
    selectItem(
      props.items.find((item) => item.id === props.selectedItemId) ?? null,
    );
  }, [props.items, selectItem, props.selectedItemId]);
  return (
    <DropdownContainer>
      <InputContainer>
        <Input placeholder={props.searchPlaceholder} {...getInputProps()} />
        <ButtonsContainer>
          {selectedItem && (
            <ButtonWithDivider
              aria-label="clear selection"
              type="button"
              onClick={() => selectItem(null)}
              tabIndex={-1}
            >
              <Icon.Close />
            </ButtonWithDivider>
          )}
          <StyledButton
            aria-label="toggle menu"
            type="button"
            $isOpen={isOpen}
            {...getToggleButtonProps()}
          >
            <Icon.Expand />
          </StyledButton>
        </ButtonsContainer>
      </InputContainer>
      <ListContainer $isOpen={isOpen} data-testid={"psychologist-dropdown"}>
        <List {...getMenuProps()}>
          {items?.map((item, index) => (
            <ListItem
              $selected={selectedItem === item}
              $highlighted={highlightedIndex === index}
              $selectable
              key={item.id}
              {...getItemProps({ item, index })}
            >
              {item.toString()}
            </ListItem>
          ))}
          {items.length === 0 && (
            <ListItem $selected={false}>
              {props.noItemsFoundPlaceholder}
            </ListItem>
          )}
        </List>
      </ListContainer>
    </DropdownContainer>
  );
};

const DropdownContainer = styled.div`
  position: relative;
  // this is will prevent from component jumping when content changes
  width: 300px;
`;

const InputContainer = styled.div`
  display: flex;
  gap: 1rem;
  background-color: #fff;
  border: 1px solid #ceccc4;
  border-radius: 100px;
  padding: 0.5rem 1rem;
  &:hover {
    border-color: ${theme.COLORS.GRAY};
    cursor: pointer;
  }
`;

const Input = styled.input`
  font-size: 16px;
  font-weight: 400;
  color: #1d1b1b;
  border: none;
  flex: 1;
  &:focus {
    outline: none;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  align-items: center;
`;

const ButtonWithDivider = styled.button`
  border: none;
  background: none;
  border-right: 1px solid ${theme.COLORS.GRAY_MEDIUM};
  display: grid;
  place-content: center;
  height: 24px;
  // fine tuned by Astrid
  // https://www.figma.com/design/QevqIXgzVebd8rWGM3wENC/Improvements?node-id=2416-23225&t=MZZQGdjPHVr96e6v-0
  padding: 0 4px 0 0;
  margin-right: 3px;
  &:hover {
    cursor: pointer;
  }
`;

const StyledButton = styled.button<CreateTransient<{ isOpen?: boolean }>>`
  border: none;
  background: none;
  display: grid;
  place-content: center;
  padding: 0;

  & > svg {
    transition: all 0.2s ease-in-out;
    transform: ${(props) => (props.$isOpen ? "rotate(180deg)" : "none")};
  }

  &:hover {
    cursor: pointer;
  }
`;

const ListContainer = styled.div<{ $isOpen?: boolean }>`
  top: 100%;
  position: absolute;
  width: 100%;
  z-index: 1;
  background-color: hsl(0, 0%, 100%);
  border-radius: 4px;
  box-shadow:
    0 0 0 1px hsla(0, 0%, 0%, 0.1),
    0 4px 11px hsla(0, 0%, 0%, 0.1);
  margin-bottom: 8px;
  margin-top: 8px;
  box-sizing: border-box;
  display: ${(props) => (props.$isOpen ? "block" : "none")};
`;

const List = styled.ul`
  max-height: 300px;
  overflow-y: auto;
  padding: 0;
`;

const ListItem = styled.li<{
  $selected: boolean;
  $selectable?: boolean;
  $highlighted?: boolean;
}>`
  padding: 0.5rem 1rem;
  font-weight: ${(props) =>
    props.$selected ? theme.FONT.WEIGHT.BOLD : theme.FONT.WEIGHT.REGULAR};
  font-family: ${theme.FONT.FAMILY.BODY};
  font-size: ${theme.FONT.SIZE.SMALL_MEDIUM};
  background-color: ${(props) =>
    props.$selectable
      ? props.$highlighted
        ? theme.COLORS.GRAY_X_LIGHT
        : "#fff"
      : "none"};

  &:hover {
    cursor: ${(props) => (props.$selectable ? "pointer" : "not-allowed")};
  }
`;
